import React, {useState, useMemo, useEffect} from 'react';
import {Box, InputAdornment, Typography, SxProps} from '@mui/material';
import TextField from '@jobseeker/lotus/elements/Input';
import Dialog from '@jobseeker/lotus/components/Dialog';
import {VersionVariants} from '@jobseeker/lotus/enums/variants';
import Image from 'next/image';
import {SearchIcon} from '@jobseeker/lotus/icons';
const isAppIimjobs = process.env.NEXT_PUBLIC_APP === 'iimjobs';

interface FunctionalArea {
  id: number;
  name: string;
  cluster?: string;
}

interface FunctionalAreaDialogV2Props {
  open: boolean;
  onClose: () => void;
  title: string;
  functionalAreasData: any;
  onChange: any;
  subtitle?: string;
  label?: string;
  className?: string;
  selectedValue?: number;
}

const FunctionalAreaDialogV2: React.FC<FunctionalAreaDialogV2Props> = ({
  open,
  onClose,
  title,
  functionalAreasData,
  onChange,
  subtitle,
  label = 'Search Functional Area',
  className = '',
  selectedValue,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  const normalizedData = useMemo(() => {
    const normalized: Record<string, FunctionalArea[]> = {};

    if (Array.isArray(functionalAreasData)) {
      functionalAreasData.forEach((area: any) => {
        const cluster = area.cluster || 'Other';
        if (!normalized[cluster]) {
          normalized[cluster] = [];
        }
        normalized[cluster].push({
          id: area.id || area.value,
          name: area.name || area.label,
          cluster: cluster,
        });
      });
    } else {
      Object.entries(functionalAreasData).forEach(
        ([category, areas]: [string, any]) => {
          normalized[category] = areas.map((area: any) => ({
            id: area.id || area.value,
            name: area.name || area.label,
            cluster: category,
          }));
        }
      );
    }

    return normalized;
  }, [functionalAreasData]);

  const allFunctionalAreas = useMemo(() => {
    const merged: FunctionalArea[] = [];
    Object.values(normalizedData).forEach(areas => {
      merged.push(...areas);
    });
    return merged;
  }, [normalizedData]);

  const categories = Object.keys(normalizedData);

  const selectedAreaCategory = useMemo(() => {
    if (!selectedValue) return null;

    for (const [category, areas] of Object.entries(normalizedData)) {
      if (areas.some(area => area.id === selectedValue)) {
        return category;
      }
    }
    return null;
  }, [selectedValue, normalizedData]);

  useEffect(() => {
    if (open && selectedValue && selectedAreaCategory) {
      setSelectedCategory(selectedAreaCategory);
    } else if (open && !selectedValue) {
      setSelectedCategory(null);
    }
  }, [open, selectedValue, selectedAreaCategory]);

  const currentAreas = selectedCategory
    ? normalizedData[selectedCategory] || []
    : allFunctionalAreas;

  const filteredAreas = searchQuery.trim()
    ? allFunctionalAreas.filter(area =>
        area.name.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : currentAreas;

  const handleCategoryClick = (category: string) => {
    setSelectedCategory(selectedCategory === category ? null : category);
    setSearchQuery('');
  };

  const handleAreaSelect = (areaId: number) => {
    onChange(areaId);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={title}
      invertedTitle={false}
      variant={VersionVariants.VERSION_SEVEN}
      subtitle={subtitle}
      className={className}
      paperMaxWidth={'562px'}
    >
      <Box
        sx={{
          height: '528px',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box sx={{p: 2, borderBottom: '1px solid #EEEEEE'}}>
          <TextField
            fullWidth
            placeholder={label}
            value={searchQuery}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Image src={SearchIcon} alt="search" width={16} height={16} />
                </InputAdornment>
              ),
            }}
            sx={{
              '.MuiInputBase-input': {
                border: 'unset !important',
                fontSize: '12px !important',
                paddingLeft: 0,
                py: '8px',
              },
              '.MuiInputBase-input::placeholder': {
                fontSize: '12px',
                fontWeight: 400,
                color: '#7E7E7E',
                opacity: 0.8,
              },
            }}
            onChange={e => setSearchQuery(e.target.value)}
            onKeyDown={e => {
              if (e.key !== 'Escape') {
                e.stopPropagation();
              }
            }}
            variant={VersionVariants.VERSION_TWO}
          />
        </Box>

        <Box sx={{display: 'flex', flex: 1, overflow: 'hidden'}}>
          <Box
            sx={{
              width: {xxs: '135px', md: '226px'},
              borderRight: '1px solid #EEEEEE',
              overflowY: 'auto',
              backgroundColor: '#FAFAFA',
            }}
          >
            {categories.map(category => (
              <Box
                key={category}
                onClick={() => {
                  handleCategoryClick(category);
                }}
                sx={{
                  padding: '21px 16px',
                  cursor: 'pointer',
                  borderBottom: '1px solid #EEEEEE',
                  //   backgroundColor:
                  //     selectedCategory === category
                  //       ? 'primary.main'
                  //       : 'transparent',
                  //   '&:hover': {
                  //     backgroundColor:
                  //       selectedCategory === category
                  //         ? 'primary.main'
                  //         : '#F5F5F5',
                  //   },
                }}
              >
                <Typography
                  sx={{
                    fontSize: '14px',
                    fontWeight: selectedCategory === category ? 500 : 400,
                    color:
                      selectedCategory === category ? '#333333' : '#7E7E7E',
                    textTransform: 'capitalize',
                  }}
                >
                  {category}
                </Typography>
              </Box>
            ))}
          </Box>

          <Box sx={{flex: 1, overflowY: 'auto', p: 1}}>
            {filteredAreas.length > 0 ? (
              filteredAreas.slice(0, 100).map(area => (
                <Box
                  key={area.id}
                  onClick={() => {
                    handleAreaSelect(area.id);
                  }}
                  sx={{
                    padding: '7px 12px',
                    cursor: 'pointer',
                    borderRadius: '4px',
                    // margin: '2px 0',
                    transition: 'background 0.2s',
                    backgroundColor:
                      selectedValue === area.id
                        ? isAppIimjobs
                          ? 'background.secondary'
                          : 'primary.light'
                        : 'transparent',
                    '&:hover': {
                      backgroundColor: isAppIimjobs
                        ? 'background.secondary'
                        : 'primary.light',
                    },
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '14px',
                      fontWeight: 400,
                      color: '#333333',
                    }}
                  >
                    {area.name}
                  </Typography>
                </Box>
              ))
            ) : (
              <Box sx={{p: 2, textAlign: 'center'}}>
                <Typography
                  sx={{
                    color: '#7E7E7E',
                    fontSize: '14px',
                    fontWeight: 400,
                  }}
                >
                  No results found
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
};

export default FunctionalAreaDialogV2;
